/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { HTMLAttributes, useEffect, useState } from 'preact/compat';
import { Container } from '@adobe-commerce/elsie/lib';
import { ShippingAddress } from '@/quote-management/data/models/negotiable-quote-model';
import { NegotiableQuoteTemplateModel } from '@/quote-management/data/models/negotiable-quote-template-model';
import { ShippingAddressDisplay as ShippingAddressDisplayComponent } from '@/quote-management/components/ShippingAddressDisplay';
import { events } from '@adobe-commerce/event-bus';

export interface ShippingAddressDisplayProps extends Omit<HTMLAttributes<HTMLDivElement>, 'loading'> {
  shippingAddress?: ShippingAddress;
  loading?: boolean;
}

export const ShippingAddressDisplay: Container<ShippingAddressDisplayProps> = ({
  shippingAddress: initialShippingAddress,
  loading,
  ...props
}) => {
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress | undefined>(initialShippingAddress);

  // Sync prop changes to internal state
  useEffect(() => {
    if (initialShippingAddress !== undefined) {
      setShippingAddress(initialShippingAddress);
    }
  }, [initialShippingAddress]);

  // Listen for quote-data events
  useEffect(() => {
    const quoteDataEvent = events.on('quote-management/quote-data', (data: { quote?: { shippingAddresses?: ShippingAddress[] } | null }) => {
      const address = data.quote?.shippingAddresses?.[0];
      setShippingAddress(address);
    }, {
      eager: true,
    });
    return () => quoteDataEvent?.off();
  }, []);

  // Listen for shipping-address-set events
  useEffect(() => {
    const shippingAddressSetEvent = events.on('quote-management/shipping-address-set', (data: { quote?: { shippingAddresses?: ShippingAddress[] } | null }) => {
      const address = data.quote?.shippingAddresses?.[0];
      setShippingAddress(address);
    }, {
      eager: true,
    });
    return () => shippingAddressSetEvent?.off();
  }, []);

  // Listen for quote-template-data events
  useEffect(() => {
    const quoteTemplateDataEvent = events.on('quote-management/quote-template-data', (data: { quoteTemplate?: NegotiableQuoteTemplateModel | null }) => {
      const address = data.quoteTemplate?.shippingAddresses?.[0];
      setShippingAddress(address);
    }, {
      eager: true,
    });
    return () => quoteTemplateDataEvent?.off();
  }, []);

  return (
    <ShippingAddressDisplayComponent
      shippingAddress={shippingAddress}
      loading={loading}
      {...props}
    />
  );
};

